import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import { 
  RefreshCw,
  Search,
  CheckCircle,
  XCircle,
  Globe,
  Copy
} from 'lucide-react';

const WithdrawalRequest = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
    totalFees: 0
  });

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const normalizeStatus = (status) => {
    const value = String(status || '').trim().toLowerCase();
    if (value === 'approved') return 'Approved';
    if (value === 'rejected') return 'Rejected';
    return 'Pending';
  };

  const buildUsersIndex = (usersData = {}) => {
    const index = {};

    Object.entries(usersData || {}).forEach(([dateKey, usersByDate]) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return;
      if (!usersByDate || typeof usersByDate !== 'object') return;

      Object.entries(usersByDate).forEach(([userKey, user]) => {
        if (!user || typeof user !== 'object') return;

        const ipAddress =
          user?.lastLoginLocation?.ip ||
          user?.lastLoginLocation?.ipAddress ||
          user?.ipAddress ||
          user?.lastLoginIP ||
          '';

        const location =
          user?.lastLoginLocation?.country ||
          user?.country ||
          '';

        const userMeta = {
          ipAddress,
          location
        };

        if (user.sponsorId) index[user.sponsorId] = userMeta;
        if (user.userId) index[user.userId] = userMeta;
        index[userKey] = userMeta;
      });
    });

    return index;
  };

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const [withdrawalsSnapshot, usersSnapshot] = await Promise.all([
        get(ref(database, 'withdrawals')),
        get(ref(database, 'users'))
      ]);
      const usersIndex = usersSnapshot.exists() ? buildUsersIndex(usersSnapshot.val()) : {};
      
      if (!withdrawalsSnapshot.exists()) {
        setRequests([]);
        updateStats([]);
        setLoading(false);
        return;
      }

      const allRequests = [];
      const data = withdrawalsSnapshot.val();

      // Iterate through date folders
      Object.entries(data).forEach(([date, users]) => {
        Object.entries(users).forEach(([userId, userRequests]) => {
          Object.entries(userRequests).forEach(([requestId, request]) => {
            const userMeta =
              usersIndex[request.userId] ||
              usersIndex[userId] ||
              usersIndex[request.sponsorId] ||
              {};

            const requestData = {
              id: requestId,
              date: date,
              userId: request.userId || userId,
              sponsorId: request.sponsorId || request.userId || userId,
              userName: request.userName || 'Unknown',
              walletType: request.walletType || 'working',
              amount: parseFloat(request.amount) || 0,
              feeAmount: parseFloat(request.feeAmount) || 0,
              netAmount: parseFloat(request.netAmount) || 0,
              crypto: request.crypto || {
                currency: 'USDT-TRC20',
                network: 'TRON Network (TRC-20)',
                address: ''
              },
              ipAddress:
                request.ipAddress ||
                request.location?.ip ||
                request.location?.ipAddress ||
                userMeta.ipAddress ||
                'N/A',
              location:
                request.location?.country ||
                request.country ||
                userMeta.location ||
                'N/A',
              status: normalizeStatus(request.status),
              requestDate: request.requestDate,
              processedDate: request.processedDate || null,
              processedBy: request.processedBy || null,
              withdrawalFee: request.withdrawalFee || 0
            };

            allRequests.push(requestData);
          });
        });
      });

      // Sort by request date (newest first)
      allRequests.sort((a, b) => new Date(b.requestDate) - new Date(a.requestDate));

      setRequests(allRequests);
      updateStats(allRequests);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (requestsData) => {
    let totalAmount = 0;
    let totalFees = 0;
    let pending = 0;
    let approved = 0;
    let rejected = 0;

    requestsData.forEach(request => {
      totalAmount += request.amount;
      totalFees += request.feeAmount;
      
      if (request.status === 'Pending') pending++;
      else if (request.status === 'Approved') approved++;
      else if (request.status === 'Rejected') rejected++;
    });

    setStats({
      total: requestsData.length,
      pending,
      approved,
      rejected,
      totalAmount,
      totalFees
    });
  };

  const updateStatus = async (requestId, date, userId, newStatus) => {
    try {
      const requestRef = ref(database, `withdrawals/${date}/${userId}/${requestId}`);
      
      await update(requestRef, {
        status: newStatus,
        processedDate: new Date().toISOString(),
        processedBy: 'admin'
      });

      // Update local state
      const updatedRequests = requests.map(req => 
        req.id === requestId 
          ? { ...req, status: normalizeStatus(newStatus), processedDate: new Date().toISOString(), processedBy: 'admin' }
          : req
      );
      
      setRequests(updatedRequests);
      updateStats(updatedRequests);

    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const filteredRequests = requests.filter(request => {
    if (statusFilter !== 'All' && request.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        request.userName?.toLowerCase().includes(term) ||
        request.userId?.toLowerCase().includes(term) ||
        request.crypto?.address?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-white mb-2">Withdrawal Requests</h1>
      <p className="text-gray-400 mb-6">Manage user withdrawal requests</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total</div>
          <div className="text-lg font-bold text-white">{stats.total}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Pending</div>
          <div className="text-lg font-bold text-yellow-400">{stats.pending}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Approved</div>
          <div className="text-lg font-bold text-green-400">{stats.approved}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Rejected</div>
          <div className="text-lg font-bold text-red-400">{stats.rejected}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Amount</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalAmount)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total Fees</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.totalFees)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user name, ID or crypto address"
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="All">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
            
            <button
              onClick={fetchWithdrawals}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white"
              title="Refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="card-glow overflow-hidden mb-6 bg-gray-800/50 rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-3 px-4 text-left text-gray-400">Date</th>
                <th className="py-3 px-4 text-left text-gray-400">User</th>
                <th className="py-3 px-4 text-left text-gray-400">Wallet</th>
                <th className="py-3 px-4 text-left text-gray-400">Amount</th>
                <th className="py-3 px-4 text-left text-gray-400">Crypto Details</th>
                <th className="py-3 px-4 text-left text-gray-400">Status</th>
                <th className="py-3 px-4 text-left text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-700/20">
                  <td className="py-3 px-4 text-sm text-gray-300">
                    {formatDate(request.requestDate)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-medium">{request.userName}</div>
                    <div className="text-xs text-gray-400">{request.userId}</div>
                    <div className="text-xs text-primary font-mono">{request.ipAddress}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      {request.location}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs ${
                      request.walletType === 'working' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                      request.walletType === 'roi' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    }`}>
                      {request.walletType}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-white font-bold">{formatCurrency(request.amount)}</div>
                    <div className="text-xs text-gray-400">Fee: {formatCurrency(request.feeAmount)}</div>
                    <div className="text-xs text-green-400">Net: {formatCurrency(request.netAmount)}</div>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <div className="text-sm text-white font-medium">{request.crypto?.currency}</div>
                      <div className="text-xs text-gray-400">{request.crypto?.network}</div>
                      <div className="flex items-start gap-2">
                        <div className="text-xs text-gray-300 break-all" title={request.crypto?.address}>
                          {request.crypto?.address || 'N/A'}
                        </div>
                        {request.crypto?.address && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(request.crypto.address)}
                            className="text-gray-400 hover:text-white mt-0.5"
                            title="Copy Address"
                            aria-label="Copy Address"
                          >
                            <Copy size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${
                      request.status === 'Pending' 
                        ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                        : request.status === 'Approved'
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }`}>
                      {request.status}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      {request.status === 'Pending' && (
                        <>
                          <button
                            onClick={() => updateStatus(request.id, request.date, request.userId, 'Approved')}
                            className="p-1.5 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30"
                            title="Approve Withdrawal"
                            aria-label="Approve Withdrawal"
                          >
                            <CheckCircle size={14} />
                          </button>
                          <button
                            onClick={() => updateStatus(request.id, request.date, request.userId, 'Rejected')}
                            className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                            title="Reject Withdrawal"
                            aria-label="Reject Withdrawal"
                          >
                            <XCircle size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredRequests.length === 0 && (
        <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
          <div className="text-4xl mb-4">💳</div>
          <h3 className="text-lg font-semibold text-white mb-2">No Withdrawal Requests</h3>
          <p className="text-gray-400">No {statusFilter === 'All' ? '' : statusFilter} requests found</p>
        </div>
      )}
    </div>
  );
};

export default WithdrawalRequest;
